"use client";

import React from "react";
import { FileText, SlidersHorizontal as Sliders, ShieldCheck, Microscope, CheckCircle2, AlertCircle, FileCheck2, ArrowRight, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { siteImage } from "@/lib/siteImages";
export default function AstmDetails() {
  const {
    lang
  } = useLanguage();
  const copy = {
    badge: {
      en: "ASTM Specification",
      zh: "ASTM 标准规范",
      es: "Especificación ASTM",
      de: "ASTM-Spezifikation",
      fr: "Spécification ASTM",
      pt: "Especificação ASTM"
    },
    stdRef: "ASTM E2016-20",
    heading: {
      en: "ASTM E2016 Standard Specification for Industrial Woven Wire Filter Cloth",
      zh: "ASTM E2016 工业编织金属丝过滤网标准规范解析",
      es: "Norma ASTM E2016 para tela de alambre tejida para filtración industrial",
      de: "ASTM E2016 Standardspezifikation für industrielles Drahtgewebe",
      fr: "Norme ASTM E2016 relative aux toiles métalliques tissées pour la filtration industrielle",
      pt: "Norma ASTM E2016 para telas de arame tecidas para filtração industrial"
    },
    lead: {
      en: "ASTM E2016 sets the definitive industrial benchmark for square opening and Dutch weave stainless steel wire cloth. It regulates dimensional tolerances, raw alloy requirements, defect classifications, and verification protocols for demanding industrial filtration.",
      zh: "ASTM E2016 为方孔及密纹（荷兰编织）不锈钢金属丝网确立了权威的工业基准。它严格规范了尺寸公差、原材料合金要求、缺陷分级及检验验证规程，确保严苛工业过滤应用的可靠性。",
      es: "La norma ASTM E2016 establece la referencia industrial para mallas de acero inoxidable de abertura cuadrada y tejido Dutch. Regula tolerancias dimensionales, requisitos de aleaciones, clasificación de defectos y protocolos de verificación para filtración industrial.",
      de: "ASTM E2016 definiert den verbindlichen industriellen Standard für Drahtgewebe mit quadratischen Maschen sowie Tressengewebe aus Edelstahl. Die Norm regelt Maßtoleranzen, Legierungsanforderungen, Fehlerklassifizierungen und Prüfprotokolle für anspruchsvolle Filtrationsanwendungen.",
      fr: "La norme ASTM E2016 constitue la référence industrielle pour les toiles métalliques en acier inoxydable à mailles carrées et tissage hollandais. Elle régit les tolérances dimensionnelles, les exigences d'alliage, les classes de défauts et les protocoles de vérification.",
      pt: "A norma ASTM E2016 estabelece a referência industrial para telas de arame de aço inoxidável com abertura quadrada e trama holandesa. Regula tolerâncias dimensionais, requisitos de ligas, classificação de defeitos e protocolos de verificação para filtração industrial."
    },
    pillars: [{
      icon: Sliders,
      title: {
        en: "Dimensional Tolerances",
        zh: "尺寸公差控制",
        es: "Tolerancias dimensionales",
        de: "Maßtoleranzen",
        fr: "Tolérances dimensionnelles",
        pt: "Tolerâncias dimensionais"
      },
      desc: {
        en: "Governs allowable deviations in mesh count per linear inch, individual aperture opening uniformity, and nominal wire diameters.",
        zh: "严格规定每线性英寸网目数、单个网孔孔径均匀度以及标称丝径的允许公差偏差。",
        es: "Regula las desviaciones admisibles en número de mallas por pulgada lineal, uniformidad de aberturas individuales y diámetros nominales de alambre.",
        de: "Regelt zulässige Abweichungen der Maschenzahl pro linearem Zoll, der Gleichmäßigkeit einzelner Maschenöffnungen und der Drahtdurchmesser.",
        fr: "Définit les écarts admissibles pour le nombre de mailles par pouce linéaire, l'uniformité des ouvertures individuelles et les diamètres nominaux de fil.",
        pt: "Regula os desvios permitidos na contagem de malha por polegada linear, uniformidade das aberturas individuais e diâmetros nominais de arame."
      }
    }, {
      icon: Microscope,
      title: {
        en: "Weave Quality & Imperfections",
        zh: "编织质量与缺陷分级",
        es: "Calidad de tejido y defectos",
        de: "Gewebequalität & Fehlstellen",
        fr: "Qualité de tissage et défauts",
        pt: "Qualidade de trama e defeitos"
      },
      desc: {
        en: "Strictly sets limits on allowable major and minor visual defects including smash, streaks, holes, drop wires, and loose picks per 100 sq ft.",
        zh: "严格限定每 100 平方英尺内允许的主要与次要外观缺陷，包括压伤、条痕、破洞、断丝与松经/松纬。",
        es: "Establece límites estrictos de defectos visuales mayores y menores por cada 100 pies cuadrados, como aplastamientos, marcas, orificios y alambres flojos.",
        de: "Begrenzt streng zulässige Haupt- und Nebenfehler je 100 Quadratfuß, einschließlich Quetschungen, Streifen, Löchern und lockeren Drähten.",
        fr: "Fixe des limites strictes pour les défauts visuels majeurs et mineurs par 100 pieds carrés, notamment écrasements, rayures, trous et fils lâches.",
        pt: "Limita rigorosamente defeitos visuais maiores e menores a cada 100 pés quadrados, incluindo amassamentos, estrias, furos e fios soltos."
      }
    }, {
      icon: ShieldCheck,
      title: {
        en: "Material & Alloy Conformity",
        zh: "材料与合金合规性",
        es: "Conformidad de materiales y aleaciones",
        de: "Werkstoff- & Legierungskonformität",
        fr: "Conformité des matériaux et alliages",
        pt: "Conformidade de materiais e ligas"
      },
      desc: {
        en: "Requires documented chemical composition and tensile testing of wire filaments conforming to ASTM A580 (SS 304, 316, 316L).",
        zh: "要求提供符合 ASTM A580 标准的金属丝化学成分及抗拉强度检测报告（如 304、316、316L 等）。",
        es: "Exige composición química documentada y ensayos de tracción de alambres conforme a ASTM A580 (SS 304, 316, 316L).",
        de: "Erfordert dokumentierte chemische Zusammensetzung und Zugprüfungen für Drähte gemäß ASTM A580 (Edelstahl 304, 316, 316L).",
        fr: "Exige une composition chimique documentée et des essais de traction des fils selon ASTM A580 (acier inoxydable 304, 316, 316L).",
        pt: "Exige composição química documentada e testes de tração de arames em conformidade com ASTM A580 (aço inoxidável 304, 316, 316L)."
      }
    }, {
      icon: FileCheck2,
      title: {
        en: "Flatness & Tension Metrics",
        zh: "平整度与张力指标",
        es: "Planitud y métricas de tensión",
        de: "Ebenheit & Spannungsmetriken",
        fr: "Planéité et tension",
        pt: "Planicidade e métricas de tensão"
      },
      desc: {
        en: "Defines standards for camber, curled edges, edge bow, and diagonal squareness to guarantee seamless OEM fabrication.",
        zh: "明确侧弯、卷边、波浪边及对角线方正度公差标准，确保 OEM 冲压加工与组装无缝配合。",
        es: "Define normas de curvatura, bordes rizados, combado lateral y escuadrado diagonal para asegurar una fabricación OEM precisa.",
        de: "Definiert Kriterien für Kantenkrümmung, eingerollte Kanten, Bogenbildung und Diagonalführung für reibungslose OEM-Weiterverarbeitung.",
        fr: "Définit les exigences de cambrure, bordures roulées, courbure et équerrage diagonal pour garantir un usinage OEM précis.",
        pt: "Define padrões de arqueamento, bordas enroladas, empenamento lateral e esquadro diagonal para garantir montagem OEM sem falhas."
      }
    }],
    tabsHeading: {
      en: "Technical Criteria & Quality Acceptance Limits",
      zh: "技术指标与质量验收公差",
      es: "Criterios técnicos y límites de aceptación de calidad",
      de: "Technische Kriterien & Qualitätsannahmegrenzen",
      fr: "Critères techniques et limites de réception qualité",
      pt: "Critérios técnicos e limites de aceitação de qualidade"
    },
    tabsSub: {
      en: "Explore dimensional boundaries, defect classes, and verification procedures.",
      zh: "深入了解尺寸界限、缺陷分类及检测验证程序。",
      es: "Explore límites dimensionales, clases de defectos y procedimientos de verificación.",
      de: "Detaillierte Einblicke in Maßgrenzen, Fehlerklassen und Prüfverfahren.",
      fr: "Explorez les limites dimensionnelles, les classes de défauts et les procédures de contrôle.",
      pt: "Explore limites dimensionais, classes de defeitos e procedimentos de verificação."
    },
    tabLabels: {
      tolerances: {
        en: "Aperture & Wire Tolerances",
        zh: "孔径与丝径公差",
        es: "Tolerancias de abertura y alambre",
        de: "Maschen- & Drahttoleranzen",
        fr: "Tolérances d'ouverture et de fil",
        pt: "Tolerâncias de abertura e arame"
      },
      classes: {
        en: "Class 1 vs Class 2 Grades",
        zh: "Class 1 与 Class 2 等级划分",
        es: "Grados Clase 1 vs Clase 2",
        de: "Klasse 1 vs. Klasse 2 Gütestufen",
        fr: "Grades Classe 1 vs Classe 2",
        pt: "Classes 1 vs Classe 2"
      },
      defects: {
        en: "Allowable Defect Criteria",
        zh: "允许缺陷验收准则",
        es: "Criterios de defectos permitidos",
        de: "Kriterien zulässiger Fehlstellen",
        fr: "Critères des défauts admissibles",
        pt: "Critérios de defeitos aceitáveis"
      }
    },
    thMeshCategory: {
      en: "Mesh Count Category",
      zh: "网目规格范围",
      es: "Categoría de conteo de malla",
      de: "Maschenweitenbereich",
      fr: "Catégorie de nombre de mailles",
      pt: "Faixa de contagem de malha"
    },
    thApertureTol: {
      en: "Average Aperture Tolerance",
      zh: "平均孔径公差",
      es: "Tolerancia de abertura promedio",
      de: "Mittlere Maschenweitentoleranz",
      fr: "Tolérance moyenne d'ouverture",
      pt: "Tolerância média de abertura"
    },
    thWireTol: {
      en: "Wire Diameter Deviation",
      zh: "金属丝径允许偏差",
      es: "Desviación de diámetro de alambre",
      de: "Drahtdurchmesser-Abweichung",
      fr: "Tolérance sur diamètre du fil",
      pt: "Desvio do diâmetro do arame"
    },
    thTypicalApp: {
      en: "Typical Process Application",
      zh: "典型工艺应用",
      es: "Aplicación de proceso típica",
      de: "Typische Prozessanwendung",
      fr: "Application industrielle type",
      pt: "Aplicação típica de processo"
    },
    toleranceRows: [{
      meshRange: {
        en: "2 to 20 Mesh",
        zh: "2 至 20 目",
        es: "2 a 20 Mesh",
        de: "2 bis 20 Mesh",
        fr: "2 à 20 Mesh",
        pt: "2 a 20 Mesh"
      },
      apertureTolerance: "± 3.0%",
      wireTolerance: "± 0.012 mm (±0.0005 in)",
      application: {
        en: "General Industrial Screening",
        zh: "常规工业筛分与固液分离",
        es: "Tamizado industrial general",
        de: "Allgemeine Industriesiebung",
        fr: "Criblage industriel standard",
        pt: "Peneiramento industrial geral"
      }
    }, {
      meshRange: {
        en: "24 to 120 Mesh",
        zh: "24 至 120 目",
        es: "24 a 120 Mesh",
        de: "24 bis 120 Mesh",
        fr: "24 à 120 Mesh",
        pt: "24 a 120 Mesh"
      },
      apertureTolerance: "± 4.5%",
      wireTolerance: "± 0.008 mm (±0.0003 in)",
      application: {
        en: "Chemical & Fluid Filtration",
        zh: "化工与流体管线过滤",
        es: "Filtración química y de fluidos",
        de: "Chemie- und Fluidfiltration",
        fr: "Filtration chimique et fluides",
        pt: "Filtração química e de fluidos"
      }
    }, {
      meshRange: {
        en: "140 to 400 Mesh",
        zh: "140 至 400 目",
        es: "140 a 400 Mesh",
        de: "140 bis 400 Mesh",
        fr: "140 à 400 Mesh",
        pt: "140 a 400 Mesh"
      },
      apertureTolerance: "± 6.0%",
      wireTolerance: "± 0.005 mm (±0.0002 in)",
      application: {
        en: "Hydraulic Fluid & Fuel Mesh",
        zh: "液压油与航空燃油过滤网",
        es: "Malla para combustible y fluidos hidráulicos",
        de: "Hydraulik- und Kraftstoffgewebe",
        fr: "Toile pour fluides hydrauliques et carburants",
        pt: "Malha para combustível e fluidos hidráulicos"
      }
    }, {
      meshRange: {
        en: "Dutch Weaves (Twilled/Plain)",
        zh: "密纹网（平纹/斜纹荷兰编织）",
        es: "Tejidos Dutch (lisos/sarga)",
        de: "Tressengewebe (glatt/Köper)",
        fr: "Tissages hollandais (unis/sergés)",
        pt: "Tramas holandesas (lisas/sarja)"
      },
      apertureTolerance: {
        en: "Per ISO / ASTM Bubble Point",
        zh: "依据 ISO / ASTM 冒泡点试验",
        es: "Según prueba de punto de burbuja ISO / ASTM",
        de: "Nach ISO / ASTM Bubble-Point-Test",
        fr: "Selon point de bulle ISO / ASTM",
        pt: "Por teste de ponto de bolha ISO / ASTM"
      },
      wireTolerance: "± 0.003 mm (±0.0001 in)",
      application: {
        en: "High Differential Pressure Filter Elements",
        zh: "高压差滤芯过滤元件",
        es: "Elementos filtrantes de alta presión diferencial",
        de: "Filterelemente für hohe Differenzdrücke",
        fr: "Éléments filtrants pour haute perte de charge",
        pt: "Elementos filtrantes de alta pressão diferencial"
      }
    }],
    toleranceNote: {
      en: "Tolerances measured in accordance with Section 7 of ASTM E2016 using precision optical projectors and calibrated micrometers under standardized 20°C ambient testing conditions.",
      zh: "公差测量依据 ASTM E2016 第 7 节标准，在 20°C 恒温标准测试环境下采用精密光学投影仪及经过校准的测微计进行检测。",
      es: "Tolerancias medidas según la Sección 7 de ASTM E2016 mediante proyectores ópticos de precisión y micrómetros calibrados a 20°C estándar.",
      de: "Toleranzmessungen nach Abschnitt 7 von ASTM E2016 mit optischen Präzisionsprojektoren und kalibrierten Mikrometern unter genormten 20°C Prüfbedingungen.",
      fr: "Mesures de tolérance réalisées selon la section 7 de l'ASTM E2016 à l'aide de projecteurs optiques de précision et micromètres étalonnés à 20°C.",
      pt: "Tolerâncias medidas conforme a Seção 7 da ASTM E2016 com projetores ópticos de precisão e micrômetros calibrados sob condições de teste padrão a 20°C."
    },
    class1Title: {
      en: "Class 1 — Industrial Grade",
      zh: "Class 1 — 工业通用级",
      es: "Clase 1 — Grado industrial",
      de: "Klasse 1 — Industriequalität",
      fr: "Classe 1 — Qualité industrielle",
      pt: "Classe 1 — Grau industrial"
    },
    class1Badge: {
      en: "General Purpose",
      zh: "通用工业用途",
      es: "Propósito general",
      de: "Universeller Einsatz",
      fr: "Usage général",
      pt: "Uso geral"
    },
    class1Desc: {
      en: "Designed for high-throughput screening, solid separation, and standard inline filtration where standard aperture deviations do not impact system performance.",
      zh: "适用于大通量筛分、固体分离及常规管线过滤，标准孔径偏差不影响系统整体运行效能。",
      es: "Diseñado para tamizado de alto caudal, separación de sólidos y filtración en línea convencional donde las tolerancias estándar no afectan el rendimiento.",
      de: "Für Siebung mit hohem Durchsatz, Feststofftrennung und Standard-Rohrleitungsfiltration ausgelegt, bei denen übliche Toleranzen die Funktion nicht beeinträchtigen.",
      fr: "Conçu pour le criblage à haut débit, la séparation solide-liquide et la filtration en ligne où les tolérances standard n'affectent pas le fonctionnement.",
      pt: "Projetado para peneiramento de alta vazão, separação de sólidos e filtração em linha comum onde as variações padrão não afetam o desempenho."
    },
    class1Points: [{
      en: "Standard opening tolerance allowances",
      zh: "符合常规开孔公差允许范围",
      es: "Tolerancias de abertura estándar",
      de: "Standardmäßige Öffnungstoleranzen",
      fr: "Tolérances d'ouverture standard",
      pt: "Tolerâncias padrão de abertura"
    }, {
      en: "General wire drawing tolerances per ASTM A580",
      zh: "执行 ASTM A580 标准拉拔丝径公差",
      es: "Tolerancias de trefilado según ASTM A580",
      de: "Allgemeine Drahtziehtoleranzen nach ASTM A580",
      fr: "Tolérances de tréfilage selon ASTM A580",
      pt: "Tolerâncias de trefilação conforme ASTM A580"
    }, {
      en: "Ideal for aggregate, coarse wastewater, and bulk straining",
      zh: "适用于骨料筛选、粗颗粒废水处理及大宗流体粗滤",
      es: "Ideal para áridos, aguas residuales y retención gruesa",
      de: "Ideal für Schüttgut, Grobwasserklärung und Grobfiltration",
      fr: "Idéal pour granulats, traitement des eaux brutes et filtration grossière",
      pt: "Ideal para agregados, efluentes brutos e retenção primária"
    }],
    class2Title: {
      en: "Class 2 — Precision Filtration Grade",
      zh: "Class 2 — 精密过滤级",
      es: "Clase 2 — Grado de filtración de precisión",
      de: "Klasse 2 — Präzisionsfiltrationsgüte",
      fr: "Classe 2 — Qualité filtration de précision",
      pt: "Classe 2 — Grau de filtração de precisão"
    },
    class2Badge: {
      en: "Critical Media",
      zh: "关键介质过滤",
      es: "Medio crítico",
      de: "Kritische Medien",
      fr: "Média critique",
      pt: "Meio crítico"
    },
    class2Desc: {
      en: "Engineered for tightly controlled particulate retention, aerospace fuel filters, pharmaceutical separation, and precision stamped disc components.",
      zh: "专为严格微粒拦截、航空燃油滤芯、制药级分离及精密冲压滤片组件设计制造。",
      es: "Diseñado para retención estricta de partículas, filtros de combustible aeroespacial, separación farmacéutica y discos estampados de precisión.",
      de: "Entwickelt für eng definierte Partikelrückhaltung, Luftfahrt-Kraftstofffilter, pharmazeutische Trennverfahren und gestanzte Präzisionsfilterscheiben.",
      fr: "Conçu pour la rétention contrôlée des particules, les filtres aéronautiques, la séparation pharmaceutique et les disques découpés de précision.",
      pt: "Desenvolvido para retenção controlada de partículas, filtros aeroespaciais de combustível, separação farmacêutica e discos estampados de precisão."
    },
    class2Points: [{
      en: "Narrowed ±3% aperture opening limits",
      zh: "更窄的 ±3% 开孔公差极限控制",
      es: "Límites estrechos de tolerancia de apertura de ±3%",
      de: "Engere Grenzwerte für Maschenweiten von ±3%",
      fr: "Limites resserrées de tolérance d'ouverture à ±3%",
      pt: "Limites estreitos de tolerância de abertura de ±3%"
    }, {
      en: "Optical aperture mapping & zero loose pick policy",
      zh: "光学孔径扫描及零松经/松纬准则",
      es: "Mapeo óptico de aberturas y cero alambres flojos",
      de: "Optische Maschenvermessung und Null-Lockerfaden-Richtlinie",
      fr: "Cartographie optique des ouvertures et tolérance zéro sur fils lâches",
      pt: "Mapeamento óptico de abertura e política de zero fios soltos"
    }, {
      en: "Lot traceability and mill test certificates (available when specified in the order)",
      zh: "批次可追溯性与材质检验证书（可按订单要求提供）",
      es: "Trazabilidad de lote y certificados de fábrica (disponibles según pedido)",
      de: "Chargenrückverfolgbarkeit und Werksprüfzeugnisse (auf Kundenanforderung lieferbar)",
      fr: "Traçabilité des lots et certificats d'usine (disponibles sur demande à la commande)",
      pt: "Rastreabilidade de lote e certificados de ensaio (disponíveis quando solicitados no pedido)"
    }],
    critDefTitle: {
      en: "Critical Defects (Zero Acceptance)",
      zh: "关键缺陷（零容忍）",
      es: "Defectos críticos (cero tolerancia)",
      de: "Kritische Fehler (Nulltoleranz)",
      fr: "Défauts critiques (tolérance zéro)",
      pt: "Defeitos críticos (tolerância zero)"
    },
    critDefPoints: [{
      en: "Broken warp or weft wires causing oversized passages",
      zh: "经丝或纬丝断丝导致孔径超差",
      es: "Alambres rotos en urdimbre o trama que generen pasos excesivos",
      de: "Kett- oder Schussdrahtbrüche mit unzulässigen Öffnungsübermaßen",
      fr: "Fils de chaîne ou de trame rompus créant des ouvertures hors tolérance",
      pt: "Fios rompidos de urdume ou trama que criem aberturas excessivas"
    }, {
      en: "Smashed areas or mechanical dents deforming aperture integrity",
      zh: "压伤区域或机械凹痕破坏孔隙几何完整性",
      es: "Zonas aplastadas o abolladuras mecánicas que deformen la abertura",
      de: "Quetschstellen oder mechanische Dellen, die die Maschengeometrie verzerren",
      fr: "Écrasements ou marques mécaniques altérant la géométrie des mailles",
      pt: "Áreas amassadas ou marcas mecânicas que deformem a geometria dos poros"
    }, {
      en: "Foreign alloy contamination or corrosive slag on wire surfaces",
      zh: "异种合金污染或金属丝表面附着腐蚀性残渣",
      es: "Contaminación con aleaciones extrañas o escoria corrosiva en el alambre",
      de: "Fremdlegierungskontamination oder korrosive Rückstände auf Drähten",
      fr: "Pollution par alliage étranger ou scories corrosives sur les fils",
      pt: "Contaminação por ligas estranhas ou escórias corrosivas nos arames"
    }, {
      en: "Oil residue or uncleaned drawing lubricants on critical runs",
      zh: "关键批次上的油污残留或未清洗的拉丝润滑剂",
      es: "Residuos de aceite o lubricantes de trefilado no eliminados en lotes críticos",
      de: "Ölrückstände oder Ziehmittelreste bei qualitätskritischen Chargen",
      fr: "Résidus d'huile ou lubrifiants de tréfilage non nettoyés sur lots critiques",
      pt: "Resíduos de óleo ou lubrificantes de trefilação não limpos em lotes críticos"
    }],
    minorDefTitle: {
      en: "Minor Imperfections (Controlled ASTM Allowances)",
      zh: "次要轻微瑕疵（符合 ASTM 允许限制）",
      es: "Imperfecciones menores (tolerancias controladas ASTM)",
      de: "Geringfügige Fehlstellen (kontrollierte ASTM-Grenzwerte)",
      fr: "Imperfections mineures (tolérances contrôlées ASTM)",
      pt: "Imperfeições menores (tolerâncias controladas ASTM)"
    },
    minorDefPoints: [{
      en: "Single wire splices in rolls over 100 linear feet (clearly tagged)",
      zh: "超过 100 线性英尺卷料中的单根接丝（附清晰标记）",
      es: "Empalmes de un solo alambre en rollos de más de 100 pies lineales (etiquetados)",
      de: "Einzeldrahtspleiße in Rollen über 100 laufenden Fuß (deutlich gekennzeichnet)",
      fr: "Raccords de fil unique sur rouleaux de plus de 100 pieds linéaires (étiquetés)",
      pt: "Emendas de fio único em rolos com mais de 100 pés lineares (identificadas)"
    }, {
      en: "Minor wire shade variation without metallurgical compromise",
      zh: "未影响冶金性能的轻微金属丝色差",
      es: "Leves variaciones de tono en el alambre sin merma metalúrgica",
      de: "Leichte Farbabweichungen des Drahts ohne metallurgische Beeinträchtigung",
      fr: "Légères variations de nuance de fil sans altération métallurgique",
      pt: "Leves variações de tonalidade sem impacto metalúrgico"
    }, {
      en: "Controlled tuck-in selvedge borders within 15 mm of roll edges",
      zh: "网卷边缘 15 mm 范围内的规整塞边处理",
      es: "Bordes remetidos controlados dentro de los 15 mm del orillo del rollo",
      de: "Kontrollierte Einlegeleisten innerhalb von 15 mm vom Rollenrand",
      fr: "Lisières rentrées contrôlées à moins de 15 mm du bord du rouleau",
      pt: "Ourelas embutidas controladas a até 15 mm das bordas do rolo"
    }, {
      en: "Transient loom reed marks within standard thickness bounds",
      zh: "厚度公差范围内的织机筘痕",
      es: "Marcas transitorias de peine de telar dentro del espesor admisible",
      de: "Vorübergehende Webblattspuren innerhalb der Dickentoleranz",
      fr: "Marques passagères de peigne de métier dans les limites d'épaisseur",
      pt: "Marcas transitórias de pente de tecelagem dentro da tolerância de espessura"
    }],
    qaBadge: {
      en: "Quality Assurance",
      zh: "质量保证体系",
      es: "Aseguramiento de la calidad",
      de: "Qualitätssicherung",
      fr: "Assurance qualité",
      pt: "Garantia da qualidade"
    },
    qaHeading: {
      en: "ASTM E2016 Inspection & Compliance Workflow",
      zh: "ASTM E2016 检验与合规流程",
      es: "Flujo de trabajo de inspección y conformidad ASTM E2016",
      de: "ASTM E2016 Prüf- und Konformitätsablauf",
      fr: "Flux de contrôle et de conformité ASTM E2016",
      pt: "Fluxo de trabalho de inspeção e conformidade ASTM E2016"
    },
    qaDesc: {
      en: "MutualFilter conducts a multi-tier inspection regime across production lots to ensure compliance with ASTM E2016 and ISO 9044 requirements before roll release or custom disc conversion.",
      zh: "MutualFilter 在生产批次中实施多级检验流程，确保在成品卷材出厂或定制滤片加工前符合 ASTM E2016 与 ISO 9044 标准要求。",
      es: "MutualFilter aplica un esquema de inspección en múltiples etapas para garantizar la conformidad con ASTM E2016 e ISO 9044 antes de la entrega o corte a medida.",
      de: "MutualFilter führt mehrstufige Prüfungen über Produktionschargen hinweg durch, um die Übereinstimmung mit ASTM E2016 und ISO 9044 vor Freigabe oder Weiterverarbeitung sicherzustellen.",
      fr: "MutualFilter met en œuvre un contrôle par étapes successives pour vérifier la conformité aux exigences ASTM E2016 et ISO 9044 avant l'expédition ou la découpe.",
      pt: "A MutualFilter conduz um regime de inspeção em múltiplos estágios para assegurar a conformidade com as normas ASTM E2016 e ISO 9044 antes da liberação dos rolos ou peças."
    },
    docCardTitle: {
      en: "Mill Test Reports & Material Traceability",
      zh: "材质检验证书与材料可追溯性",
      es: "Informes de ensayo de fábrica y trazabilidad",
      de: "Werksprüfzeugnisse & Materialrückverfolgbarkeit",
      fr: "Certificats de contrôle d'usine et traçabilité",
      pt: "Relatórios de ensaio de fábrica e rastreabilidade"
    },
    docCardDesc: {
      en: "Comprehensive dimensional inspection sheets, alloy heat numbers, and ASTM compliance documentation are available when specified in the order for your quality records.",
      zh: "完整的尺寸检验记录、合金炉号及 ASTM 合规文件均可按订单要求提供，便于客户质量部门归档溯源。",
      es: "Hojas de inspección dimensional, coladas de aleación y documentación de conformidad ASTM disponibles según pedido para sus archivos de calidad.",
      de: "Detaillierte Maßprüfprotokolle, Schmelzen-Nummern und ASTM-Konformitätsdokumente sind auf Kundenwunsch für Ihre Qualitätssicherung verfügbar.",
      fr: "Relevés de contrôle dimensionnel, numéros de coulée et documents de conformité ASTM disponibles sur demande pour vos dossiers de qualité.",
      pt: "Fichas de medição dimensional, números de corrida e documentação de conformidade ASTM disponíveis mediante solicitação no pedido."
    },
    imgAlt: {
      en: "Microscopic optical inspection of precision stainless steel wire mesh apertures",
      zh: "精密不锈钢丝网网孔的高倍光学显微检验",
      es: "Inspección óptica microscópica de aberturas de malla metálica de acero inoxidable",
      de: "Optische Mikroskopprüfung von Edelstahl-Drahtgewebemaschen",
      fr: "Contrôle optique microscopique des ouvertures de toile métallique en acier inoxydable",
      pt: "Inspeção óptica microscópica de aberturas de malha de arame de aço inoxidável"
    },
    imgCaption: {
      en: "Calibrated 50x optical coordinate verification of warp and weft opening geometry.",
      zh: "50倍放大校准光学坐标系统对经纬向网孔几何尺寸的精密测量。",
      es: "Verificación óptica calibrada a 50x de la geometría de abertura en urdimbre y trama.",
      de: "Kalibrierte optische Koordinatenvermessung (50x) der Kett- und Schussöffnungsgeometrie.",
      fr: "Contrôle optique étalonné à 50x de la géométrie des ouvertures de chaîne et trame.",
      pt: "Verificação óptica calibrada em 50x da geometria das aberturas de urdume e trama."
    },
    inspectionSteps: [{
      id: "step-1",
      title: {
        en: "1. Optical Calibrated Coordinate Measurement",
        zh: "1. 高精度光学影像坐标测量",
        es: "1. Medición por coordenadas ópticas calibradas",
        de: "1. Optische kalibrierte Koordinatenmessung",
        fr: "1. Mesure optique par coordonnées étalonnées",
        pt: "1. Medição por coordenadas ópticas calibradas"
      },
      content: {
        en: "Computerized optical vision systems verify aperture sizing across both warp and weft directions at 50x magnification to calculate standard deviation and mean aperture rating.",
        zh: "计算机光学视觉检测系统在 50 倍放大倍率下，对经纬向孔径尺寸进行全面测量，计算标准偏差及平均孔隙等级。",
        es: "Sistemas de visión óptica computarizados verifican la abertura en urdimbre y trama a 50x para calcular la desviación estándar y el valor medio.",
        de: "Rechnergestützte optische Kamerasysteme prüfen die Maschenöffnungen in Kett- und Schussrichtung bei 50-facher Vergrößerung zur Ermittlung von Mittelwert und Standardabweichung.",
        fr: "Des systèmes de vision optique assistés par ordinateur contrôlent les ouvertures en chaîne et trame à un grossissement de 50x pour calculer la moyenne et l'écart-type.",
        pt: "Sistemas computadorizados de visão óptica medem as aberturas nas direções de urdume e trama a 50x para calcular a média e o desvio padrão."
      }
    }, {
      id: "step-2",
      title: {
        en: "2. Positive Material Identification (PMI) & Alloy Verification",
        zh: "2. 材料成分分析 (PMI) 与合金验证",
        es: "2. Identificación positiva de materiales (PMI) y aleaciones",
        de: "2. Werkstoffidentifikation (PMI) & Legierungsprüfung",
        fr: "2. Identification positive des matériaux (PMI) et alliages",
        pt: "2. Identificação positiva de materiais (PMI) e ligas"
      },
      content: {
        en: "Non-destructive X-ray Fluorescence (XRF) testing validates that raw wire composition matches heat-traceable stainless steel alloy limits (Chromium, Nickel, Molybdenum levels per grade).",
        zh: "采用无损 X 射线荧光光谱 (XRF) 检测，验证原始金属丝成分符合可追溯炉号的不锈钢合金公差范围（对应各材质等级的铬、镍、钼含量）。",
        es: "Ensayos no destructivos por fluorescencia de rayos X (XRF) verifican que la composición del alambre cumpla los límites de la aleación trazable por colada.",
        de: "Zerstörungsfreie Röntgenfluoreszenzanalyse (RFA) stellt sicher, dass die Drahtzusammensetzung den Grenzwerten der schmelzrückverfolgbaren Edelstahllegierungen entspricht.",
        fr: "Des analyses non destructives par spectrométrie de fluorescence X (SFX) vérifient la composition du fil selon les spécifications d'alliage traçables par coulée.",
        pt: "Ensaios não destrutivos de fluorescência de raios X (FRX) validam se a composição do arame atende aos limites da liga rastreável por corrida."
      }
    }, {
      id: "step-3",
      title: {
        en: "3. Bubble Point Testing for Dutch & Micron Filter Cloth",
        zh: "3. 密纹滤网与微米滤布冒泡点测试",
        es: "3. Ensayo de punto de burbuja para mallas Dutch y de micras",
        de: "3. Bubble-Point-Prüfung für Tressen- und Feinstfiltergewebe",
        fr: "3. Essai de point de bulle pour toiles hollandaises et seuils microniques",
        pt: "3. Teste de ponto de bolha para telas holandesas e de precisão"
      },
      content: {
        en: "Evaluates capillary pressure required to displace fluid from pore channels, determining the nominal micron rating and checking for aperture flaws in high-density weave patterns.",
        zh: "评估驱除微孔通道液体所需的毛细管压力，从而精确测定高密度编织结构的标称微米精度与单一孔隙异常。",
        es: "Evalúa la presión capilar necesaria para desplazar el fluido de los poros, determinando la retención en micras y detectando posibles irregularidades en tejidos densos.",
        de: "Ermittelt den Kapillardruck zur Flüssigkeitsverdrängung aus den Porenkanälen, um den nominellen Mikronwert zu bestimmen und Unregelmäßigkeiten im dichten Gewebe aufzuspüren.",
        fr: "Mesure la pression capillaire requise pour chasser le fluide des canaux poreux afin d'évaluer le seuil micronique et détecter d'éventuels défauts d'ouverture.",
        pt: "Mede a pressão capilar necessária para deslocar o fluido dos canais de poros, determinando o valor nominal em mícrons e detectando imperfeições em tramas densas."
      }
    }, {
      id: "step-4",
      title: {
        en: "4. Physical Surface & Weave Defect Audit",
        zh: "4. 物理表面与编织缺陷全幅检验",
        es: "4. Inspección de superficie física y defectos de tejido",
        de: "4. Visuelle Oberflächen- und Webfehlerprüfung",
        fr: "4. Contrôle de surface physique et défauts de tissage",
        pt: "4. Auditoria de superfície física e defeitos de tecelagem"
      },
      content: {
        en: "Full-width roll inspection under synchronized polarized lighting to enforce strict thresholds on snarls, foreign inclusion, or wire crossovers.",
        zh: "在同步偏光照明下进行全幅卷材连续检验，严格执行对扭结、异物夹杂或交叉叠丝的检验阈值。",
        es: "Inspección continua a todo el ancho del rollo bajo iluminación polarizada sincronizada para controlar torceduras, inclusiones o cruces de alambre.",
        de: "Prüfung über die volle Rollenbreite unter synchronisierter Polarisationsbeleuchtung zur strikten Vermeidung von Schlingen, Fremdeinschlüssen oder Drahtkreuzungen.",
        fr: "Inspection continue sur toute la largeur du rouleau sous éclairage polarisé synchronisé afin d'éliminer vrilles, corps étrangers ou chevauchements de fils.",
        pt: "Inspeção contínua em toda a largura do rolo sob iluminação polarizada sincronizada para controlar dobras, inclusões estranhas ou cruzamentos de fios."
      }
    }],
    ctaCardHeading: {
      en: "Need Custom Tolerances or ASTM Technical Verification?",
      zh: "需要定制公差或 ASTM 技术规范验证？",
      es: "¿Requiere tolerancias a medida o verificación técnica ASTM?",
      de: "Benötigen Sie Sondermaße oder technische Prüfung nach ASTM?",
      fr: "Besoin de tolérances sur mesure ou d'une vérification technique ASTM ?",
      pt: "Precisa de tolerâncias personalizadas ou verificação técnica ASTM?"
    },
    ctaCardDesc: {
      en: "Our engineering team assists with ASTM E2016 specification validation, aperture micron conversion, and lot-specific test certificates available upon order specification.",
      zh: "我们的工程团队可为客户项目提供 ASTM E2016 规范验证、孔径微米换算以及按订单要求的批次材质检验证书支持。",
      es: "Nuestro equipo de ingeniería le ayuda con la validación de especificaciones ASTM E2016, conversión de apertura a micras y certificados de ensayo según pedido.",
      de: "Unser Engineering-Team unterstützt Sie bei der Spezifikationsprüfung nach ASTM E2016, der Umrechnung von Maschenweite in Mikron und auftragsbezogenen Prüfzertifikaten.",
      fr: "Notre équipe technique vous accompagne pour la validation selon l'ASTM E2016, la conversion d'ouverture en microns et les certificats de contrôle sur commande.",
      pt: "Nossa equipe de engenharia auxilia na validação de especificações ASTM E2016, conversão de abertura em mícrons e certificados específicos de lote conforme pedido."
    },
    ctaQuoteBtn: {
      en: "Request ASTM Mesh Quote",
      zh: "索取 ASTM 丝网报价",
      es: "Solicitar cotización de malla ASTM",
      de: "ASTM-Drahtgewebe anfragen",
      fr: "Demander un devis toile ASTM",
      pt: "Solicitar cotação de malha ASTM"
    },
    ctaStandardsBtn: {
      en: "View All Standards",
      zh: "查看全部标准",
      es: "Ver todas las normas",
      de: "Alle Normen ansehen",
      fr: "Voir toutes les normes",
      pt: "Ver todas as normas"
    }
  };
  return <section id="astmstandarddetails" data-nav="light" className="bg-muted/40 text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl mb-10 lg:mb-12">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline" className="text-primary border-primary/30 uppercase tracking-wide font-mono text-[13px]">
                {pick(lang, copy.badge)}
              </Badge>
              <span className="mf-caption text-muted-foreground font-mono">
                {copy.stdRef}
              </span>
            </div>
            <h2 className="mf-h2 font-default text-foreground mb-4">
              {pick(lang, copy.heading)}
            </h2>
            <p className="mf-lead text-muted-foreground">
              {pick(lang, copy.lead)}
            </p>
          </div>
        </Reveal>

        {/* 4 Core Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {copy.pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <Reveal key={i} delay={Math.min(i * 60, 300)}>
                <Card className="h-full bg-card border border-border mf-hover-lift">
                  <CardHeader className="pb-3">
                    <div className="w-10 h-10 rounded-md bg-muted text-primary border border-border flex items-center justify-center mb-3">
                      <IconComponent className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {pick(lang, pillar.title)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pick(lang, pillar.desc)}
                    </p>
                  </CardContent>
                </Card>
              </Reveal>;
        })}
        </div>

        {/* Deep Dive Interactive Tabs */}
        <Reveal>
          <div className="bg-card border border-border rounded-lg p-6 sm:p-8 mb-16">
            <div className="mb-6 pb-6 border-b border-border">
              <h3 className="mf-h3 font-default text-card-foreground">
                {pick(lang, copy.tabsHeading)}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                {pick(lang, copy.tabsSub)}
              </p>
            </div>

            <Tabs defaultValue="tolerances" className="w-full">
              <TabsList className="mb-6 flex-wrap h-auto gap-2 p-1.5 bg-muted">
                <TabsTrigger value="tolerances" className="mf-caption sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {pick(lang, copy.tabLabels.tolerances)}
                </TabsTrigger>
                <TabsTrigger value="classes" className="mf-caption sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {pick(lang, copy.tabLabels.classes)}
                </TabsTrigger>
                <TabsTrigger value="defects" className="mf-caption sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {pick(lang, copy.tabLabels.defects)}
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: Tolerances Table */}
              <TabsContent value="tolerances" className="space-y-4">
                <div className="rounded-lg border border-border overflow-hidden bg-card">
                  <div className="overflow-x-auto">
                    <Table className="min-w-[640px]">
                      <TableHeader className="bg-muted">
                        <TableRow>
                          <TableHead className="font-semibold text-foreground py-3 px-4">
                            {pick(lang, copy.thMeshCategory)}
                          </TableHead>
                          <TableHead className="font-semibold text-foreground py-3 px-4">
                            {pick(lang, copy.thApertureTol)}
                          </TableHead>
                          <TableHead className="font-semibold text-foreground py-3 px-4">
                            {pick(lang, copy.thWireTol)}
                          </TableHead>
                          <TableHead className="font-semibold text-foreground py-3 px-4">
                            {pick(lang, copy.thTypicalApp)}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {copy.toleranceRows.map((row, i) => <TableRow key={i} data-index={i}>
                            <TableCell className="font-mono text-sm font-medium text-foreground py-3 px-4">
                              {pick(lang, row.meshRange)}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground font-mono py-3 px-4">
                              {typeof row.apertureTolerance === "string" ? row.apertureTolerance : pick(lang, row.apertureTolerance)}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground font-mono py-3 px-4">
                              {row.wireTolerance}
                            </TableCell>
                            <TableCell className="text-sm text-foreground py-3 px-4">
                              {pick(lang, row.application)}
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <div className="flex items-start gap-2 mf-caption text-muted-foreground pt-2">
                  <Info className="w-4 h-4 shrink-0 text-primary mt-0.5" />
                  <p className="max-w-3xl">{pick(lang, copy.toleranceNote)}</p>
                </div>
              </TabsContent>

              {/* Tab 2: Grade Classification */}
              <TabsContent value="classes" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-5 rounded-lg border border-border bg-card">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-base font-semibold text-foreground">
                        {pick(lang, copy.class1Title)}
                      </h4>
                      <Badge variant="outline" className="bg-muted text-foreground">
                        {pick(lang, copy.class1Badge)}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {pick(lang, copy.class1Desc)}
                    </p>
                    <ul className="space-y-2 text-sm text-foreground">
                      {copy.class1Points.map((pt, i) => <li key={i} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                          <span>{pick(lang, pt)}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="p-5 rounded-lg border border-primary/30 bg-primary/5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-base font-semibold text-foreground">
                        {pick(lang, copy.class2Title)}
                      </h4>
                      <Badge variant="default">
                        {pick(lang, copy.class2Badge)}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {pick(lang, copy.class2Desc)}
                    </p>
                    <ul className="space-y-2 text-sm text-foreground">
                      {copy.class2Points.map((pt, i) => <li key={i} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                          <span>{pick(lang, pt)}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </TabsContent>

              {/* Tab 3: Defect Evaluation */}
              <TabsContent value="defects" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-5 rounded-lg border border-border bg-card">
                    <div className="flex items-center gap-2 text-destructive mb-3">
                      <AlertCircle className="w-4 h-4" />
                      <h5 className="font-semibold text-sm">
                        {pick(lang, copy.critDefTitle)}
                      </h5>
                    </div>
                    <ul className="mf-caption text-muted-foreground space-y-2 list-disc list-inside">
                      {copy.critDefPoints.map((pt, i) => <li key={i}>{pick(lang, pt)}</li>)}
                    </ul>
                  </div>

                  <div className="p-5 rounded-lg border border-border bg-card">
                    <div className="flex items-center gap-2 text-foreground mb-3">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      <h5 className="font-semibold text-sm">
                        {pick(lang, copy.minorDefTitle)}
                      </h5>
                    </div>
                    <ul className="mf-caption text-muted-foreground space-y-2 list-disc list-inside">
                      {copy.minorDefPoints.map((pt, i) => <li key={i}>{pick(lang, pt)}</li>)}
                    </ul>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </Reveal>

        {/* Verification Accordion & Quality Protocol with Side Visual */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          <div className="lg:col-span-5 space-y-6">
            <Reveal>
              <div>
                <Badge variant="outline" className="mb-3 text-primary border-primary/30">
                  {pick(lang, copy.qaBadge)}
                </Badge>
                <h3 className="mf-h2 font-default text-foreground mb-4">
                  {pick(lang, copy.qaHeading)}
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                  {pick(lang, copy.qaDesc)}
                </p>
              </div>
            </Reveal>

            {/* Supporting Inspection Microscopic Visual */}
            <Reveal delay={100}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
                <div className="mf-card-media mf-ratio-card relative w-full overflow-hidden">
                  <img src={siteImage("b0f5bf-astm-microscope-inspe-331a0e.webp")} alt={pick(lang, copy.imgAlt)} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
                <div className="p-4 bg-card">
                  <p className="mf-caption text-muted-foreground">
                    {pick(lang, copy.imgCaption)}
                  </p>
                </div>
              </Card>
            </Reveal>

            <Reveal delay={160}>
              <div className="p-4 rounded-lg border border-border bg-card space-y-2">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary shrink-0" />
                  <span className="text-sm font-semibold text-foreground">
                    {pick(lang, copy.docCardTitle)}
                  </span>
                </div>
                <p className="mf-caption text-muted-foreground">
                  {pick(lang, copy.docCardDesc)}
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <Accordion type="single" collapsible defaultValue="step-1" className="w-full bg-card border border-border rounded-lg px-4 sm:px-6">
                {copy.inspectionSteps.map((step, i) => <AccordionItem key={step.id} value={step.id} data-index={i}>
                    <AccordionTrigger className="text-base font-semibold text-card-foreground hover:no-underline hover:text-primary">
                      {pick(lang, step.title)}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {pick(lang, step.content)}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </Reveal>
          </div>
        </div>

        {/* Technical Callout CTA Banner */}
        <Reveal>
          <div className="rounded-lg border border-border bg-card p-6 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="max-w-2xl">
              <h4 className="mf-h3 font-default text-foreground mb-2">
                {pick(lang, copy.ctaCardHeading)}
              </h4>
              <p className="text-sm text-muted-foreground">
                {pick(lang, copy.ctaCardDesc)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Button asChild variant="default" size="lg">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, copy.ctaQuoteBtn)}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/StandardsAndInspectionPage" className="inline-flex items-center gap-2">
                  <FileCheck2 className="w-4 h-4" />
                  <span>{pick(lang, copy.ctaStandardsBtn)}</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/AstmE2016ScopeLimitationsPage" className="inline-flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  <span>
                    {pick(lang, {
                    en: "Read what ASTM E2016 does not cover",
                    zh: "了解 ASTM E2016 未涵盖的范围与局限",
                    es: "Lea lo que no cubre la norma ASTM E2016",
                    de: "Erfahren Sie, was ASTM E2016 nicht abdeckt",
                    fr: "Découvrez ce que l'ASTM E2016 ne couvre pas",
                    pt: "Leia o que a norma ASTM E2016 não cobre"
                  })}
                  </span>
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}